import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  TextField,
  Divider,
  Autocomplete,
  Stack,
  FormControlLabel,
  Checkbox,
  Avatar,
  Tooltip,
  Pagination,
  IconButton,
} from "@mui/material";
import {
  getEvidenceDataForProvider,
  downloadProviderEvidenceData,
  updateEvidenceComment,
  getEvidenceDataForProviderQuarterly,
  deleteProviderEvidence,
} from "services/api";
import { useAuth } from "context/authContext";
import LoadingScreen from "components/Loading";
import { fieldStyles } from "services/utils";
import SnackbarWrapper from "components/Snackbar";
import { saveEvidenceData } from "services/api";
import { useTranslation } from "react-i18next";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Download } from "@mui/icons-material";
import DownloadingIcon from "@mui/icons-material/Downloading";
import EvidenceCommentButton from "components/EvidenceCommentButton";
import { Clear } from "@mui/icons-material";
import ConfirmationDialog from "components/ConfirmationDialog";

export default function EvidenceProviderPage() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2022 + 1 }, (_, i) => 2023 + i);
  const snackbarRef = useRef();
  const { employee, selectedMonth, selectedYear, quarter, isQuarterly } = useAuth();
  const [view, setView] = useState("byJob");
  const [evidenceData, setEvidenceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [jobFilter, setJobFilter] = useState("");
  const [kpiFilter, setKpiFilter] = useState("");
  const [jobTitles, setJobTitles] = useState([]);
  const [kpiNames, setKpiNames] = useState([]);
  const [onlyUnassessed, setOnlyUnassessed] = useState(false);
  const [evidenceComments, setEvidenceComments] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // number of employees (groups) per page
  const [evidences, setEvidences] = useState({});
  const [confOpen, setConfOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    const fetchEvidenceData = async () => {
      try {
        setLoading(true);

        let evidenceDataForProvider;
        if (isQuarterly) {
          evidenceDataForProvider = await getEvidenceDataForProviderQuarterly(
            employee.id,
            quarter,
            selectedYear
          );
        } else {
          evidenceDataForProvider = await getEvidenceDataForProvider(
            employee.id,
            selectedMonth,
            selectedYear
          );
        }

        if (evidenceDataForProvider !== "error") {
          // Store evidence data for provider
          setEvidenceData(evidenceDataForProvider);

          // Extract unique job titles
          const jobTitles = [...new Set(evidenceDataForProvider.map((row) => row[3]))];
          setJobTitles(jobTitles);

          // Extract unique KPI names
          const kpiNames = [...new Set(evidenceDataForProvider.map((row) => row[4]))];
          setKpiNames(kpiNames);

          //  Build evidence map
          const evidenceFileNameMap = {};
          const evidenceCommentMap = {};
          evidenceDataForProvider.forEach((row) => {
            const evidenceId = row[0]; // evidence provider record id
            const empId = row[1]; // id of the employee being assessed
            const fileName = row[7]; // or whatever field represents your “value”
            const comment = row[8];

            if (fileName != null) {
              evidenceFileNameMap[`${evidenceId}_${empId}`] = fileName;
              // evidenceFileNameMap[`${evidenceId}_${empId}`] = { file: null, fileName };
            }
            evidenceCommentMap[`${evidenceId}_${empId}`] = comment || "";
          });
          // ("Evidence File Name Map:", evidenceFileNameMap);
          setEvidences(evidenceFileNameMap);
          setEvidenceComments(evidenceCommentMap);
        }
      } catch (error) {
        console.error("Error fetching evidence data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (employee?.id) {
      fetchEvidenceData();
    }
  }, [employee?.id, selectedMonth, selectedYear, quarter, isQuarterly]);

  const handleChangeView = (event, newView) => {
    if (newView) setView(newView);
  };
  const handleConfOpen = (evidenceId, empId, month, year) => {
    // Store the evidence to delete in state
    setPendingDelete({ evidenceId, empId, month, year });
    setConfOpen(true); // open the confirmation dialog
  };

  const handleSaveEvidence = async (id, file) => {
    const [evidenceIdStr, employeeIdStr] = id.split("_");
    const evidenceId = parseInt(evidenceIdStr, 10);
    const employeeId = employeeIdStr;

    // Determine month for quarterly or monthly
    const month = !isQuarterly ? selectedMonth : quarter >= 1 && quarter <= 4 ? quarter * 3 : 0;
    if (month === 0) return;

    try {
      const res = await saveEvidenceData({
        evidenceId,
        employeeId,
        month,
        year: selectedYear,
        comment: "",
        file, // pass the actual File object
      });

      if (res !== "error") {
        snackbarRef.current?.showSnackbar(t("user.competencies.saveEvidence"), "success");

        setEvidences((prev) => ({
          ...prev,
          [id]: res.fileName || file.name, // store just the filename
        }));
      } else {
        snackbarRef.current?.showSnackbar(t("user.evidenceProvider.saveError"), "error");
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(t("user.evidenceProvider.saveError"), "error");
      console.error(err);
    }
  };

  const handleDownloadEvidence = async (evidenceId, empId, fileName) => {
    if (!fileName) return;
    setDownloadLoading(true);
    try {
      // Optional: show loading state
      await downloadProviderEvidenceData(evidenceId, empId, selectedMonth, selectedYear, fileName);
    } catch (err) {
      console.error("Error downloading evidence:", err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSaveEvidenceComment = async (evidenceId, empId, newComment) => {
    try {
      // Call the API to update the comment
      await updateEvidenceComment(evidenceId, empId, selectedMonth, selectedYear, newComment);

      // Update local state so UI reflects the change immediately
      setEvidenceComments((prev) => ({
        ...prev,
        [`${evidenceId}_${empId}`]: newComment,
      }));

      snackbarRef.current?.showSnackbar(t("user.evidenceProvider.commentSaved"), "success");
    } catch (err) {
      console.error("Error saving evidence comment:", err);
      snackbarRef.current?.showSnackbar(t("user.evidenceProvider.commentError"), "error");
    }
  };

  const handleDeleteEvidence = async (evidenceId, empId, month, year) => {
    setLoading(true);
    const result = await deleteProviderEvidence(evidenceId, empId, month, year);

    if (result !== "error") {
      setEvidenceData((prev) =>
        prev.map((row) => {
          if (row[0] === evidenceId && row[1] === empId) {
            const newRow = [...row];
            newRow[5] = null;
            newRow[6] = null;
            newRow[7] = null; // clear fileName
            newRow[8] = ""; // clear comment
            newRow[9] = null; // clear file content if stored
            return newRow;
          }
          return row;
        })
      );

      // Clear file in evidences map instead of deleting the key
      setEvidences((prev) => ({
        ...prev,
        [`${evidenceId}_${empId}`]: null, // was deleting before
      }));

      // Clear comment in evidenceComments map instead of deleting the key
      setEvidenceComments((prev) => ({
        ...prev,
        [`${evidenceId}_${empId}`]: "", // empty string instead of deleting
      }));

      snackbarRef.current?.showSnackbar(t("user.evidenceProvider.evidenceDelete"), "success");
    } else {
      // enqueueSnackbar("Failed to delete evidence", { variant: "error" });
      snackbarRef.current?.showSnackbar(t("user.evidenceProvider.evidenceDeleteFail"), "error");
    }

    setLoading(false);
  };

  const {
    groupedByJob,
    groupedByKPI,
    paginatedJobs,
    paginatedKPIs,
    totalJobGroups,
    totalKPIGroups,
  } = useMemo(() => {
    const groupedByJob = {};
    const groupedByKPI = {};

    evidenceData?.forEach(
      ([
        evidenceId,
        empId,
        empName,
        jobTitle,
        kpiName,
        fileType,
        fileSize,
        backendFileName,
        comment,
        fileData,
        target,
        image,
      ]) => {
        if (onlyUnassessed && backendFileName != null) return;
        // If filtering by job or kpi → skip rows that don't match.
        if (view === "byJob" && jobFilter && jobFilter !== jobTitle) return;
        if (view === "byKPI" && kpiFilter && kpiFilter !== kpiName) return;

        // Use live filename from state if available
        const id = `${evidenceId}_${empId}`;
        const liveFileName = evidences[id] ?? backendFileName;
        const liveComment = evidenceComments[id] ?? comment;

        // Group by Job, job[empName]: data
        if (!groupedByJob[jobTitle]) groupedByJob[jobTitle] = {};
        if (!groupedByJob[jobTitle][empName]) groupedByJob[jobTitle][empName] = [];
        groupedByJob[jobTitle][empName].push({
          evidenceId,
          empId,
          kpiName,
          fileName: liveFileName,
          comment: liveComment,
          target,
          image,
        });

        // Group by Kpi, kpi[jobTitle]: data
        if (!groupedByKPI[kpiName]) groupedByKPI[kpiName] = [];
        groupedByKPI[kpiName].push({
          evidenceId,
          empId,
          empName,
          jobTitle,
          fileName: liveFileName,
          comment: liveComment,
          target,
          image,
        });
      }
    );

    // converting to array
    const jobEntries = Object.entries(groupedByJob);
    const kpiEntries = Object.entries(groupedByKPI);

    const paginatedJobs = jobEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const paginatedKPIs = kpiEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return {
      groupedByJob,
      groupedByKPI,
      paginatedJobs,
      paginatedKPIs,
      totalJobGroups: jobEntries.length,
      totalKPIGroups: kpiEntries.length,
    };
  }, [
    evidenceData,
    evidences,
    onlyUnassessed,
    jobFilter,
    kpiFilter,
    view,
    page,
    rowsPerPage,
    evidenceComments,
  ]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>
        {t("user.evidenceProvider.evidenceData")}
      </Typography>

      {/* Toggle Buttons */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <ToggleButtonGroup value={view} exclusive onChange={handleChangeView}>
          <ToggleButton value="byJob">{t("user.evidenceProvider.byJob")}</ToggleButton>
          <ToggleButton value="byKPI">{t("user.evidenceProvider.byKpi")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Filters */}
      <Box mt={2} width="100%" mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <Box width={{ xs: "100%", sm: "auto", minWidth: 250 }}>
          {view === "byJob" && (
            <Autocomplete
              size="small"
              value={jobFilter}
              onChange={(e, newValue) => setJobFilter(newValue || "")}
              options={["", ...jobTitles]}
              getOptionLabel={(option) => option || "All"}
              renderInput={(params) => (
                <TextField {...params} label={t("user.functional.filterByJob")} sx={fieldStyles} />
              )}
            />
          )}

          {view === "byKPI" && (
            <Autocomplete
              size="small"
              value={kpiFilter}
              onChange={(e, newValue) => setKpiFilter(newValue || "")}
              options={["", ...kpiNames]}
              getOptionLabel={(option) => option || "All"}
              renderInput={(params) => (
                <TextField {...params} label={t("user.functional.filterByKPI")} sx={fieldStyles} />
              )}
            />
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={onlyUnassessed}
              onChange={(e) => setOnlyUnassessed(e.target.checked)}
            />
          }
          label={t("user.evidenceProvider.noEvidence")}
        />
      </Box>

      {/* BY JOB VIEW */}
      {view === "byJob"
        ? paginatedJobs.map(([jobTitle, employees]) => (
            <Paper key={jobTitle} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" mb={1}>
                {jobTitle}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Loop over data of the job */}
              {Object.entries(employees).map(([empName, kpis]) => (
                <Box key={empName} mb={2}>
                  {/* Emp Name and Image */}
                  <Box display="flex" alignItems="center" mb={1}>
                    <Avatar
                      src={kpis[0]?.image ? `data:image/jpeg;base64,${kpis[0].image}` : undefined}
                      alt={empName}
                      sx={{ width: 32, height: 32, mr: 1 }}
                    />
                    <Typography variant="subtitle1">{empName}</Typography>
                  </Box>

                  <Box display="flex" flexWrap="wrap" gap={2} justifyContent="flex-start">
                    {kpis.map(({ evidenceId, empId, kpiName, fileName }) => (
                      <Box
                        key={evidenceId + "_" + empId}
                        sx={{
                          width: "100%",
                          maxWidth: 280,
                          flexGrow: 1,
                        }}
                      >
                        <Stack spacing={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {/* KPI NAME BOX */}
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                backgroundColor: "#f5f5f5",
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              <Tooltip title={kpiName}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {kpiName}
                                </Typography>
                              </Tooltip>
                            </Box>

                            {/* ATTACH ICON */}
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <input
                                type="file"
                                id={`file-input-${evidenceId}_${empId}`}
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  // Update the evidences map with both file object and fileName
                                  handleSaveEvidence(evidenceId + "_" + empId, file);
                                }}
                              />
                              {/* <Tooltip title={fileName || "Upload file"} arrow> */}
                              <Tooltip
                                title={
                                  evidences[evidenceId + "_" + empId] ||
                                  t("user.evidenceProvider.uploadFile")
                                }
                                arrow
                              >
                                <IconButton
                                  component="span"
                                  onClick={() =>
                                    document
                                      .getElementById(`file-input-${evidenceId}_${empId}`)
                                      .click()
                                  }
                                  size="small"
                                  color={fileName ? "success" : "default"}
                                >
                                  <AttachFileIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  fileName
                                    ? t("user.evidenceProvider.download")
                                    : t("user.evidenceProvider.noFile")
                                }
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  disabled={!fileName}
                                  color={fileName ? "primary" : "default"}
                                  onClick={() =>
                                    handleDownloadEvidence(evidenceId, empId, fileName)
                                  }
                                >
                                  {downloadLoading ? <DownloadingIcon /> : <Download />}
                                </IconButton>
                              </Tooltip>
                              <EvidenceCommentButton
                                evidence={{
                                  evidenceId,
                                  employeeId: empId,
                                  month: selectedMonth,
                                  year: selectedYear,
                                  comment: evidenceComments?.[`${evidenceId}_${empId}`] ?? "",
                                }}
                                isEditable={true}
                                disabled={!fileName}
                                onSave={(newComment) =>
                                  handleSaveEvidenceComment(evidenceId, empId, newComment)
                                }
                              />
                              <Tooltip title={t("user.evidenceProvider.remove")} arrow>
                                <IconButton
                                  size="small"
                                  color={fileName ? "error" : "default"}
                                  disabled={!fileName}
                                  onClick={() =>
                                    handleConfOpen(evidenceId, empId, selectedMonth, selectedYear)
                                  }
                                >
                                  <Clear fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Paper>
          ))
        : /* BY KPI VIEW */
          paginatedKPIs.map(([kpiName, employees]) => (
            <Paper key={kpiName} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" mb={1}>
                {kpiName}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" flexWrap="wrap" gap={2} justifyContent="flex-start">
                {employees.map(({ evidenceId, empId, empName, jobTitle, fileName, image }) => (
                  <Box
                    key={evidenceId + "_" + empId}
                    sx={{
                      width: "100%",
                      maxWidth: 240,
                      flexGrow: 1,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={image ? `data:image/jpeg;base64,${image}` : undefined}
                          alt={empName}
                          sx={{ width: 32, height: 32 }}
                        />
                        <Tooltip title={`${empName} (${jobTitle})`}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              maxWidth: 160,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {empName}
                          </Typography>
                        </Tooltip>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("admin.strategicInfo.initiatives.jobTitle")}: {jobTitle}
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1}>
                        <input
                          type="file"
                          id={`file-input-${evidenceId}_${empId}`}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            // Update the evidences map with both file object and fileName
                            handleSaveEvidence(evidenceId + "_" + empId, file);
                          }}
                        />
                        <Tooltip
                          title={
                            evidences[evidenceId + "_" + empId] ||
                            t("user.evidenceProvider.uploadFile")
                          }
                          arrow
                        >
                          <IconButton
                            component="span"
                            onClick={() =>
                              document.getElementById(`file-input-${evidenceId}_${empId}`).click()
                            }
                            size="small"
                            color={fileName ? "success" : "default"}
                          >
                            <AttachFileIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            fileName
                              ? t("user.evidenceProvider.download")
                              : t("user.evidenceProvider.noFile")
                          }
                          arrow
                        >
                          <IconButton
                            size="small"
                            disabled={!fileName}
                            color={fileName ? "primary" : "default"}
                            onClick={() => handleDownloadEvidence(evidenceId, empId, fileName)}
                          >
                            {downloadLoading ? <DownloadingIcon /> : <Download />}
                          </IconButton>
                        </Tooltip>
                        <EvidenceCommentButton
                          evidence={{
                            evidenceId,
                            employeeId: empId,
                            month: selectedMonth,
                            year: selectedYear,
                            comment: evidenceComments?.[`${evidenceId}_${empId}`] ?? "",
                          }}
                          isEditable={true}
                          disabled={!fileName}
                          onSave={(newComment) =>
                            handleSaveEvidenceComment(evidenceId, empId, newComment)
                          }
                        />
                        <Tooltip title={t("user.evidenceProvider.remove")} arrow>
                          <IconButton
                            size="small"
                            color={fileName ? "error" : "default"}
                            disabled={!fileName}
                            onClick={() =>
                              handleConfOpen(evidenceId, empId, selectedMonth, selectedYear)
                            }
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}

      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        message="Are you sure you want to delete Evidence?"
        onConfirm={() => {
          if (pendingDelete) {
            handleDeleteEvidence(
              pendingDelete.evidenceId,
              pendingDelete.empId,
              pendingDelete.month,
              pendingDelete.year
            );
            setPendingDelete(null);
          }
          setConfOpen(false);
        }}
      />

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil((view === "byJob" ? totalJobGroups : totalKPIGroups) / rowsPerPage)}
          page={page + 1}
          onChange={(e, newPage) => setPage(newPage - 1)}
          color="primary"
        />
      </Box>

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
}
